import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import Helpers from "../Config/Helpers";
import { Link, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Paper,
  Typography,
  TextField,
  Stack,
  Breadcrumbs,
  Link as MuiLink,
  Grid,
  Chip,
} from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import PaymentsIcon from "@mui/icons-material/Payments";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PeopleIcon from "@mui/icons-material/People";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import ReusableDataGrid from "../Components/ReusableDataGrid";

const toLocalDateInput = (value = new Date()) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatCurrency = (value) =>
  `$${Number(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const hasTime = hours !== 0 || minutes !== 0;
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    ...(hasTime
      ? {
          hour: "numeric",
          minute: "2-digit",
        }
      : {}),
  });
};

function DailyPaymentHistory() {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(() => toLocalDateInput());
  const [report, setReport] = useState({
    summary: {},
    payments: [],
  });
  const [loading, setLoading] = useState(true);

  const getReport = useCallback(() => {
    setLoading(true);
    const tzOffsetMinutes = new Date().getTimezoneOffset();
    const query = `?date=${encodeURIComponent(selectedDate)}&tzOffsetMinutes=${tzOffsetMinutes}`;

    axios
      .get(
        `${Helpers.baseUrl}/reports/daily-payments/${Helpers.authParentId}${query}`,
        Helpers.headers,
      )
      .then((response) => {
        setReport({
          summary: response.data?.summary || {},
          payments: Array.isArray(response.data?.payments) ? response.data.payments : [],
        });
      })
      .catch((error) => {
        Helpers.unauthenticated(error, navigate);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [navigate, selectedDate]);

  useEffect(() => {
    getReport();
  }, [getReport]);

  const summary = useMemo(() => report.summary || {}, [report.summary]);

  const statCards = useMemo(
    () => [
      {
        title: "Payments",
        value: `${Number(summary.paymentCount || 0)}`,
        secondary: "Payment events",
        icon: <PaymentsIcon sx={{ fontSize: 34 }} />,
        color: "#2563eb",
        bg: "rgba(37, 99, 235, 0.10)",
      },
      {
        title: "Invoices",
        value: `${Number(summary.invoiceCount || 0)}`,
        secondary: "Invoices touched",
        icon: <ReceiptLongIcon sx={{ fontSize: 34 }} />,
        color: "#7c3aed",
        bg: "rgba(124, 58, 237, 0.10)",
      },
      {
        title: "Customers",
        value: `${Number(summary.customerCount || 0)}`,
        secondary: "Customers paid",
        icon: <PeopleIcon sx={{ fontSize: 34 }} />,
        color: "#0f766e",
        bg: "rgba(15, 118, 110, 0.10)",
      },
      {
        title: "Collected",
        value: formatCurrency(summary.total),
        secondary: "All methods",
        icon: <AttachMoneyIcon sx={{ fontSize: 34 }} />,
        color: "#059669",
        bg: "rgba(5, 150, 105, 0.10)",
      },
      {
        title: "Card",
        value: formatCurrency(summary.card),
        secondary: "Card payments",
        icon: <CreditCardIcon sx={{ fontSize: 34 }} />,
        color: "#4f46e5",
        bg: "rgba(79, 70, 229, 0.10)",
      },
      {
        title: "Cash",
        value: formatCurrency(summary.cash),
        secondary: "Cash payments",
        icon: <PointOfSaleIcon sx={{ fontSize: 34 }} />,
        color: "#d97706",
        bg: "rgba(217, 119, 6, 0.10)",
      },
      {
        title: "Check",
        value: formatCurrency(summary.check),
        secondary: "Check payments",
        icon: <RequestQuoteIcon sx={{ fontSize: 34 }} />,
        color: "#b45309",
        bg: "rgba(180, 83, 9, 0.10)",
      },
      // {
      //   title: "Online / Other",
      //   value: formatCurrency(
      //     Number(summary.online || 0) + Number(summary.other || 0),
      //   ),
      //   secondary: "Online and other",
      //   icon:
      //     Number(summary.online || 0) > 0 ? (
      //       <LanguageIcon sx={{ fontSize: 34 }} />
      //     ) : (
      //       <HelpOutlineIcon sx={{ fontSize: 34 }} />
      //     ),
      //   color: "#475467",
      //   bg: "rgba(71, 84, 103, 0.10)",
      // },
    ],
    [summary],
  );

  const paymentColumns = [
    {
      field: "paymentDate",
      headerName: "Paid At",
      width: 150,
      renderCell: (params) => formatDateTime(params.value),
    },
    {
      field: "customer",
      headerName: "Customer",
      minWidth: 170,
      flex: 1,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "receiptNumber",
      headerName: "Invoice",
      width: 110,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "vehicle",
      headerName: "Vehicle",
      minWidth: 170,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "amount",
      headerName: "Amount",
      width: 115,
      renderCell: (params) => formatCurrency(params.value),
    },
    {
      field: "paymentType",
      headerName: "Method",
      width: 120,
      renderCell: (params) => {
        const value = params.value || "Unknown";
        const paletteByType = {
          card: { bg: "#e0e7ff", color: "#4338ca" },
          cash: { bg: "#fef3c7", color: "#92400e" },
          check: { bg: "#ffedd5", color: "#9a3412" },
          online: { bg: "#dbeafe", color: "#1d4ed8" },
          other: { bg: "#e5e7eb", color: "#374151" },
        };
        const key = String(value).trim().toLowerCase();
        const palette = paletteByType[key] || paletteByType.other;
        return (
          <Chip
            label={value}
            size="small"
            sx={{
              fontWeight: 600,
              backgroundColor: palette.bg,
              color: palette.color,
            }}
          />
        );
      },
    },
    {
      field: "remaining",
      headerName: "Remaining",
      width: 120,
      renderCell: (params) => formatCurrency(params.value),
    },
    {
      field: "status",
      headerName: "Receipt Status",
      width: 140,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "note",
      headerName: "Notes",
      minWidth: 220,
      flex: 1,
      renderCell: (params) => params.value || "-",
    },
  ];

  return (
    <Box
      sx={{
        pt: { xs: 10, sm: 11, md: 12 },
        pb: 3,
        minHeight: "100vh",
        backgroundColor: "#f5f7fa",
      }}
    >
      <Container
        maxWidth={false}
        sx={{
          maxWidth: "100% !important",
          px: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            p: 3,
            background:
              "linear-gradient(135deg, #059669 0%, #0891b2 55%, #2563eb 100%)",
            borderRadius: 2,
            color: "white",
          }}
        >
          <Breadcrumbs
            separator={
              <NavigateNextIcon
                fontSize="small"
                sx={{ color: "rgba(255,255,255,0.7)" }}
              />
            }
            sx={{ mb: 2 }}
          >
            <MuiLink
              component={Link}
              to="/user/dashboard"
              underline="hover"
              sx={{
                display: "flex",
                alignItems: "center",
                color: "rgba(255,255,255,0.9)",
              }}
            >
              <HomeIcon sx={{ mr: 0.5, fontSize: 18 }} />
              Home
            </MuiLink>
            <MuiLink
              component={Link}
              to="/user/reports"
              underline="hover"
              sx={{
                display: "flex",
                alignItems: "center",
                color: "rgba(255,255,255,0.9)",
              }}
            >
              Reports
            </MuiLink>
            <Typography sx={{ color: "white", fontWeight: 600 }}>
              Daily Payment History
            </Typography>
          </Breadcrumbs>

          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "flex-start", md: "center" }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <PaymentsIcon sx={{ fontSize: 38 }} />
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  Daily Payment History
                </Typography>
                <Typography sx={{ color: "rgba(255,255,255,0.88)" }}>
                  See who paid, how they paid, and which invoice each payment was applied to.
                </Typography>
              </Box>
            </Stack>
          </Stack>
        </Paper>

        <Paper elevation={1} sx={{ p: 2, borderRadius: 2, mb: 3 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", md: "center" }}
          >
            <TextField
              label="Date"
              type="date"
              size="small"
              value={selectedDate}
              onChange={(event) => setSelectedDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <Button
              variant="contained"
              onClick={getReport}
              sx={{ textTransform: "none", whiteSpace: "nowrap" }}
            >
              Refresh Report
            </Button>
          </Stack>
        </Paper>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {statCards.map((card) => (
            <Grid item xs={12} sm={6} lg={3} key={card.title}>
              <Card elevation={0} sx={{ borderRadius: 2, height: "100%" }}>
                <CardContent>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                  >
                    <Box>
                      <Typography variant="body2" sx={{ color: "#6b7280", mb: 0.5 }}>
                        {card.title}
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: "#111827" }}>
                        {card.value}
                      </Typography>
                      <Typography variant="body2" sx={{ color: card.color, fontWeight: 600 }}>
                        {card.secondary}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        width: 52,
                        height: 52,
                        borderRadius: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: card.color,
                        backgroundColor: card.bg,
                      }}
                    >
                      {card.icon}
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Paper elevation={1} sx={{ borderRadius: 2, p: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
            Payment Activity
          </Typography>
          <ReusableDataGrid
            rows={report.payments}
            loading={loading}
            columns={paymentColumns}
            pageSize={10}
            pageSizeOptions={[10, 25, 50, 100]}
            noRowsMessage="No payments were recorded for this day"
            disableInternalScroll
          />
        </Paper>
      </Container>
    </Box>
  );
}

export default DailyPaymentHistory;
